import { InsuranceType } from '@weave/schema-gen-ts/dist/schemas/insurance-verification/shared/enums.pb';
import { PatientMismatchFieldDetail } from '@weave/schema-gen-ts/dist/schemas/insurance-verification/v2/insurance_verification_adapter.pb';

export enum NetworkType {
  InNetwork = 'in_network',
  OutOfNetwork = 'out_of_network',
}
export enum NetworkName {
  outOfNetwork = 'OUT OF NETWORK',
  inNetwork = 'IN NETWORK',
}

export enum PeriodUnit {
  Year = 'year',
  Month = 'month',
  Lifetime = 'lifetime',
}

export enum CoverageStatus {
  Active = 'active',
  // Add other possible statuses
}

export enum Source {
  Website = 'website',
  Edi = 'edi',
}

export interface TuuthFairyAPIResponse {
  lastVerifiedAt: string;
  coverage: Coverage;
  details: null;
  insuranceRequestDetails: InsuranceRequestDetails;
  dentalSummaryInfo: DentalSummaryInfo;
  isAutoVerification: boolean;
  isEligibleForWebsiteResp: boolean;
  partner: string;
  patientMismatchDetails?: PatientMismatchFieldDetail[];
}

export interface Coverage {
  status: CoverageStatus;
  effectiveDate: string;
  endDate: string;
}

export interface InsuranceRequestDetails {
  personId: string;
  payerId: string;
  payerName: string;
  memberId: string;
  firstName: string;
  lastName: string;
  birthDate: string;
  groupNumber: string;
  groupName: string;
  nationalProviderId: string;
  providerTaxId: string;
  isDependent: boolean;
  relationship: string;
  subscriberFirstName: string;
  subscriberLastName: string;
  subscriberDateOfBirth: string;
  subscriberGroupNumber: string;
  subscriberMemberId: string;
  pmsPayerId: string;
  procedureCodes: string[];
  appointmentId: string;
  appointmentDate: string;
}

export interface DentalSummaryInfo {
  createdAt: string;
  id: string;
  location: {
    id: string;
  };
  locationId: string;
  metadata: null;
  nextRetryAt: string;
  patient: DentalSummaryPatient;
  payerId: string;
  payerName: string;
  plan: Plan;
  priority: string;
  provider: Provider;
  requestBody: RequestBody;
  requests: Request[];
  retryCount: number;
  retryEnabled: boolean;
  subscriber: DentalSummarySubscriber;
}

export interface DentalSummaryPatient {
  dob: string;
  firstName: string;
  groupNumber: string;
  lastName: string;
  memberId: string;
  payerId: string;
  zip?: string;
}
export interface Service {
  code: string;
  category: string;
  date: string;
  toothNumber: string | null;
  toothSurface: string | null;
  toothDescription: string | null;
  source: string;
}

export interface CategoryCoverage {
  category: string;
  maxCoverage: number;
  minCoverage: number;
  networkName: string;
  networkType: NetworkType;
}

export interface Plan {
  assignedOffice: AssignedOffice;
  benefits: Benefit[];
  categoryCoverages: CategoryCoverage[];
  coordinationOfBenefits: string;
  deductibles: Deductible[];
  effectiveDate: string;
  endDate: string;
  groupName: string;
  groupNumber: string;
  hasAlternateBenefits: boolean;
  hasMissingToothClause: boolean;
  hasTOA: boolean;
  insuranceType: string;
  maximums: Maximum[];
  multiVisitsPaidOn: string;
  networkName: string;
  networkType: string;
  networks: Network[];
  payerId: string;
  payerName: string;
  planName: string;
  provisions: string[];
  renewalType: string;
  services: Service[];
  startDate: string;
  status: string;
}

export interface Provider {
  id: string;
  name: string;
  npi: string;
  pin: string;
  state: string;
  tin: string;
}

export interface RequestBody {
  locationId: string;
  metadata: null;
  patient: RequestBodyPatient;
  payerId: string;
  provider: Provider;
  retryEnabled: boolean;
  subscriber: RequestBodySubscriber;
}

export interface Request {
  error: string;
  source: Source;
  status: string;
}

export interface DentalSummarySubscriber {
  dob: string;
  firstName: string;
  groupNumber: string;
  lastName: string;
  memberId: string;
  payerId: string;
  zip?: string;
}

export interface AssignedOffice {
  city: string;
  line: string;
  name: string;
  state: string;
  zip: string;
}

export interface Benefit {
  ageMax: number;
  ageMin: number;
  category: string;
  coPay: number;
  code: string;
  coverage: number;
  deductibleApplies: boolean;
  description: string;
  limit: number;
  limitUnit: string;
  networkName: string;
  networkType: NetworkType;
  notes: string[];
  period: number;
  periodUnit: PeriodUnit;
  source: Source;
  waitingPeriod: number;
  waitingPeriodUnit: string;
  serviceDates?: string[]; //This data is not sent by the tuuthfairy we constructed it in api-helpers.ts
  alternateBenefitCode?: string;
  alternateBenefitReason?: string;
}

export interface Deductible {
  amount: number | string;
  category: string;
  coverageLevel: string;
  networkName: string;
  networkType: NetworkType;
  periodUnit: PeriodUnit;
  remaining: number | string;
  source: Source;
}

export interface Maximum {
  amount: number | string;
  category: string;
  coverageLevel: string;
  networkName: string;
  networkType: NetworkType;
  periodUnit: PeriodUnit;
  remaining: number | string;
  source: Source;
}

export interface Network {
  id: string;
  name: string;
  type: NetworkType;
}

export interface RequestBodyPatient {
  dob: string;
  firstName: string;
  groupNumber: string;
  lastName: string;
  memberId: string;
  zipCode: string;
}

export interface RequestBodySubscriber {
  dob: string;
  firstName: string;
  groupNumber: string;
  lastName: string;
  memberId: string;
  zipCode: string;
}

export type ReportModalState = {
  searchValue: string;
  filterValue: string;
  insuranceType: InsuranceType;
  setSearchValue: (value: string) => void;
  setFilterValue: (value: string) => void;
  setInsuranceType: (type: InsuranceType) => void;
};

export type PayerListDetails = {
  id: string;
  name: string;
  alternatePayerIds: string[];
  websiteId?: string;
};

export type MismatchField =
  | 'PATIENT_DOB'
  | 'PATIENT_MEMBER_ID'
  | 'PATIENT_NAME'
  | 'SUBSCRIBER_DOB'
  | 'SUBSCRIBER_MEMBER_ID'
  | 'SUBSCRIBER_NAME';

export interface MismatchItem {
  field: MismatchField;
  requestValue: string;
  planValue: string;
}

export type MismatchMap = Partial<
  Record<
    MismatchField,
    {
      requestValue: string;
      planValue: string;
    }
  >
>;
